import { HubspotListDialog } from "@/components/hubspot-list-dialog";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { CompanyDTO, HubSpotListDTO, HubSpotSaveToListResultDTO } from "@shaarp/common";
import { useState } from "react";
import { toast, useToastError } from "../ui/use-toast";

type CompaniesHubspotListExportProps = {
  companies: CompanyDTO[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompaniesSynced?: (syncedRecords: { id: string; externalId: string; directLink: string | null }[]) => void;
};

export function CompaniesHubspotListExport({
  companies,
  open,
  onOpenChange,
  onCompaniesSynced,
}: CompaniesHubspotListExportProps) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (selectedList: HubSpotListDTO) => {
    const companiesIds = companies.map((c) => c.id);
    setIsSaving(true);
    try {
      const { data: result } = await api.post<HubSpotSaveToListResultDTO>(
        `/integration/crm/hubspot/company-lists/${selectedList.listId}/records`,
        { companiesIds, listName: selectedList.name }
      );
      onOpenChange(false);
      onCompaniesSynced?.(result.syncedRecords);
      toast({
        variant: "default",
        title: t("components.dataTableCompanies.hubspotList.successTitle"),
        description:
          result.failedCount > 0
            ? t(
                "components.dataTableCompanies.hubspotList.successDescriptionWithFailures",
                {
                  success: result.successCount,
                  listName: result.listName,
                  failed: result.failedCount,
                }
              )
            : t(
                "components.dataTableCompanies.hubspotList.successDescription",
                {
                  success: result.successCount,
                  listName: result.listName,
                }
              ),
      });
    } catch {
      toastError();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <HubspotListDialog
      type="company"
      open={open}
      onOpenChange={onOpenChange}
      count={companies.length}
      isSaving={isSaving}
      onSave={handleSave}
      translationPrefix="components.dataTableCompanies.hubspotList"
    />
  );
}
